/* eslint-disable import/no-anonymous-default-export */
import * as React from "react";
import { Switch, Route, useRouteMatch, Redirect } from "react-router-dom";
import { Alert, AlertProps } from "react-bootstrap";
import { useForm } from "react-yup";
import * as Yup from "yup";

import { CustomError, PageEnvelope, Analytics } from "@skv/core";
import { usePageEnvelope } from "@skv/state";
import { useUserState } from "~/state";
import { Field } from "~/ui";

const SCHEMA = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Please enter an email address")
    .nullable(),
}).defined();

interface SignInViewModel {
  email: string;
  emailSent: boolean;
  hasSignedOut: boolean;
  tokenIsExpired: boolean;
  tokenIsInvalid: boolean;
  tokenIsUsed: boolean;
}

export default (): ReturnType<React.FC<unknown>> => {
  const match = useRouteMatch();
  const { isAuthenticated } = useUserState();

  const {
    pageEnvelope: { data },
  } = usePageEnvelope<SignInViewModel>();

  const [viewModel, setViewModel] = React.useState(() => {
    if (data) {
      return data;
    }

    return {
      email: null,
      emailSent: false,
    };
  });
  const [serverError, setServerError] = React.useState<AlertProps>(() => {
    if (data?.tokenIsExpired) {
      return {
        show: true,
        variant: "danger",
        children: "The token provided has expired. Please try again.",
      };
    }

    if (data?.tokenIsInvalid) {
      return {
        show: true,
        variant: "danger",
        children: "The token provided is invalid. Please try again.",
      };
    }

    if (data?.tokenIsUsed) {
      return {
        show: true,
        variant: "danger",
        children: "The token provided has already been used. Please try again.",
      };
    }

    if (data?.hasSignedOut) {
      return {
        show: true,
        variant: "success",
        children: "You have successfully signed out.",
      };
    }

    return {
      show: false,
      variant: null,
      children: null,
    };
  });

  const alertRef = React.useRef<HTMLDivElement>(null);
  const formRef = React.useRef(null);

  const {
    values,
    FormProvider,
    createSubmitHandler,
    setSubmitting,
    isSubmitting,
    field,
    setTouched,
  } = useForm({
    validationSchema: SCHEMA,
  });

  const handleSubmit = React.useMemo(() => {
    return createSubmitHandler((v) => {
      setServerError((prev) => ({
        ...prev,
        show: false,
      }));

      fetch("/signin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(v),
      })
        .then<PageEnvelope<SignInViewModel>>((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("Response not ok");
        })
        .then(({ success, data, errorCode, errorMessage }) => {
          if (success) {
            setViewModel({
              email: data.email,
              emailSent: data.emailSent,
            });
            return;
          }

          switch (errorCode) {
            default:
              throw Error(
                "Response ok, but server did not respond with a known error code"
              );
          }
        })
        .catch((err: Error | CustomError) => {
          // if (err instanceof CustomError) {
          //   setServerError(true);
          // }
          setServerError((prev) => ({
            ...prev,
            show: true,
            variant: "danger",
            children: (
              <>
                Sorry we could not send an email at this time. Please try again.
              </>
            ),
          }));
        })
        .finally(() => {
          setSubmitting(false);
        });
    });
  }, [createSubmitHandler, setSubmitting]);

  if (isAuthenticated) {
    return <Redirect to="/my-activity" />;
  }

  const analytics = React.useMemo(() => {
    const _data = {
      subSection: "signin",
      subSubSection: "signin",
      subSubSubSection: "signin",
      pageTitle: "signin",
      pageType: "form",
      loginStatus: "loggedout" as const,
    };

    if (data?.tokenIsInvalid) {
      _data.subSubSection += ":invalid";
      _data.subSubSubSection += ":invalid";
    }

    if (data?.tokenIsExpired) {
      _data.subSubSection += ":expired";
      _data.subSubSubSection += ":expired";
    }

    if (data?.hasSignedOut) {
      _data.subSubSection += ":signedout";
      _data.subSubSubSection += ":signedout";
    }

    return _data;
  }, [data]);

  const success = !!(viewModel.emailSent && viewModel.email);

  return (
    <Switch>
      <Route exact path={match.path}>
        <div className="row">
          <aside className="col-md-4 bg-light py-4 mb-3">
            {success ? (
              <>
                <h2>Email sent</h2>

                <ol className="mb-0">
                  <li className="mb-2">
                    Check your <b>{viewModel.email}</b> inbox for an email from
                    us.
                  </li>
                  <li className="mb-2">
                    Push the button to confirm your email address.
                  </li>
                  <li className="mb-2">You will be signed in.</li>
                </ol>
              </>
            ) : (
              <>
                <h2>Sign in</h2>
                <p>We will send you an email to confirm your email address.</p>
                <Alert
                  ref={alertRef}
                  dismissible
                  onClose={(e) => {
                    setServerError((prev) => ({
                      ...prev,
                      show: false,
                    }));
                  }}
                  {...serverError}
                />
                <FormProvider>
                  <form
                    ref={formRef}
                    method="POST"
                    action="/signin"
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <Field
                      {...field}
                      id="signin-email"
                      name="email"
                      label="Email address"
                      autoComplete="email"
                      className="mb-3"
                      aria-required
                      value={values.email || ""}
                      onChange={(e) => {
                        field.onChange(e);
                        setTouched((prev) => ({ ...prev, email: false }), true);
                      }}
                      onBlur={(e) => {
                        field.onBlur(e);
                        setTouched(
                          (prev) => ({ ...prev, email: false }),
                          false
                        );
                      }}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send email"}
                    </button>
                  </form>
                </FormProvider>
                <Analytics data={analytics} />
              </>
            )}
          </aside>
          {!success && (
            <section className="col-md-7 offset-md-1 py-md-4">
              <h1 className="h2">Sign in to see your activity</h1>
              <p>
                When you sign in with your email address, you will be able to
                see...
              </p>
              <ul className="list-dash">
                <li className="mb-2">
                  all of the searches you have subscribed to using that email
                  address.
                </li>
                <li className="mb-2">
                  all of your applications using that email address.
                </li>
                <li className="mb-2">
                  all of your favourites on different devices.
                </li>
              </ul>
            </section>
          )}
        </div>
      </Route>
    </Switch>
  );
};
